import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  ButtonSelect,
  MenuNested,
  MenuNestedItem,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import {
  APPLIED_COL_FILTERS_MODEL,
  COL_FILTERS_MODEL,
} from '../../../models/CertsModels';
import { BUTTON_KINDS } from '../../../page/CertificationDetails/CertsControlsGroup/constants';
import {
  getCertDetailsData,
  setCertTriggerFromSearchOrFilter,
  setGroupByOptions,
} from '../../../redux/certdetails/action';
import {
  getItemsApiOffset,
  SCREEN_SIZE_TABLET_MEDIUM,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import { getMessageKey } from '../../../utils/utils';
import ColumnsFilter from '../ColumnsFilter/ColumnsFilter';
import DynamicSort from '../DynamicSort';
import RowsFilter from '../RowsFilter/RowsFilter';

import './CertsControlsMore.css';

// TODO: Savi in More drop down is still a WIP
// TODO: Sort in More drop down is still a WIP

function CertsControlsMore({
  options,
  setIsMoreOpen,
  // Sort
  handleDynamicSort,
  resetDynamicSort,
  // Filter
  showFilterBar,
  setShowFilterBar,
  // Group By
  groupByOptions,
  // Rows
  rowFilters,
  appliedRowFilters,
  setAppliedRowFilters,
  // Columns
  colFilters,
  setColFilters,
  appliedColFilters,
  setAppliedColFilters,
  setIsDraggingDndItem,
}) {
  const dispatch = useDispatch();
  const intl = useIntl();

  const { SAVI, FILTER, GROUP_BY, ROWS, COLUMNS, SORT } = BUTTON_KINDS;

  const isSaviInMore = options.includes(SAVI);
  const isFilterInMore = options.includes(FILTER);
  const isGroupByInMore = options.includes(GROUP_BY);
  const isRowsInMore = options.includes(ROWS);
  const isColumnsInMore = options.includes(COLUMNS);
  const isSortInMore = options.includes(SORT);

  const {
    core: { columnNames, searchText, isLoading },
    global: { mediaQueryValue, certKey },
    tabs: { tabData },
  } = useSelector((state) => state?.certifications?.details);

  const activeTabCount = tabData?.find((tab) => tab.isActive)?.count;
  const { messages } = useSelector((state) => state?.user);
  const tabletMediumQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_MEDIUM
  );
  const isTabletMediumWidth = tabletMediumQuery.value <= mediaQueryValue;

  const [filtersOption, setFiltersOption] = useState({
    label:
      messages?.CERTS_FILTER || localize(intl, getMessageKey('CERTS_FILTER')),
    value: 'filters',
    isChecked: showFilterBar,
    icon: 'Filter',
  });

  const handleFilterBar = () => {
    setFiltersOption((prev) => ({ ...prev, isChecked: !prev.isChecked }));
    setShowFilterBar(!showFilterBar);
  };

  const handleGroupByChange = (selectedOption) => {
    const updatedGroupByOptions = groupByOptions.map((option) => ({
      ...option,
      isChecked: option.value === selectedOption.value,
    }));

    dispatch(setGroupByOptions(updatedGroupByOptions));
    dispatch(setCertTriggerFromSearchOrFilter(true));
    dispatch(
      getCertDetailsData(
        certKey,
        columnNames,
        getItemsApiOffset(activeTabCount),
        0,
        searchText
      )
    );
  };

  const getMenuTrigger = () => (
    <ButtonSelect
      className='CertsControlsMore-trigger'
      kind='rectangle'
      label='More'
      size='medium'
      isDisabled={activeTabCount === 0 || isLoading}
    />
  );

  const initialPlacement = 'left-start';
  const fallbackPlacementsForMenus = ['right-start', 'bottom'];

  return (
    <MenuNested trigger={getMenuTrigger()} setIsExpanded={setIsMoreOpen}>
      {isSaviInMore ? (
        <MenuNested
          label='Copilot'
          prefixIconKind='SaviCompanionGradient'
          itemClassName='CertsControlsMore-saviItem'
          placement={initialPlacement}
          fallbackPlacements={fallbackPlacementsForMenus}>
          Savi in More drop down is still a WIP
        </MenuNested>
      ) : null}
      {isFilterInMore ? (
        <MenuNestedItem
          option={filtersOption}
          onChange={handleFilterBar}
          shouldCloseMenuOnItemClick
          placement={initialPlacement}
          fallbackPlacements={fallbackPlacementsForMenus}
        />
      ) : null}
      {isGroupByInMore ? (
        <MenuNested
          prefixIconKind='Dashboard'
          label={
            messages?.CERTS_GROUP_BY ||
            localize(intl, getMessageKey('CERTS_GROUP_BY'))
          }
          options={groupByOptions}
          onOptionChange={handleGroupByChange}
          placement={initialPlacement}
          fallbackPlacements={fallbackPlacementsForMenus}
          shouldCloseMenuOnItemClick
        />
      ) : null}
      {isRowsInMore ? (
        <MenuNested
          label={
            messages?.CERTS_ROWS || localize(intl, getMessageKey('CERTS_ROWS'))
          }
          prefixIconKind='Rows'
          placement={initialPlacement}
          fallbackPlacements={fallbackPlacementsForMenus}>
          <RowsFilter
            filters={rowFilters}
            appliedRowFilters={appliedRowFilters}
            setAppliedRowFilters={setAppliedRowFilters}
          />
        </MenuNested>
      ) : null}
      {isColumnsInMore && !isTabletMediumWidth ? (
        <MenuNested
          label={
            messages?.CERTS_COLUMNS ||
            localize(intl, getMessageKey('CERTS_COLUMNS'))
          }
          prefixIconKind='Columns'
          placement={initialPlacement}
          fallbackPlacements={fallbackPlacementsForMenus}
          classNames={{
            dropdownClassName: 'CertsControlsMore-columnsDropdown',
            itemsClassName: 'CertsControlsMore-columnsItems',
          }}>
          <ColumnsFilter
            isInMore
            colFilters={colFilters}
            setColFilters={setColFilters}
            appliedColFilters={appliedColFilters}
            setAppliedColFilters={setAppliedColFilters}
            setIsDraggingDndItem={setIsDraggingDndItem}
          />
        </MenuNested>
      ) : null}
      {/* WIP Not updated hidden for Beta */}
      {isSortInMore ? (
        <MenuNested
          label={
            messages?.CERTS_SORT || localize(intl, getMessageKey('CERTS_SORT'))
          }
          prefixIconKind='Sort'
          placement={initialPlacement}
          fallbackPlacements={fallbackPlacementsForMenus}
          classNames={{
            dropdownClassName: 'CertsControlsMore-sortDropdown',
          }}>
          <DynamicSort
            rowFilters={rowFilters}
            colFilters={colFilters}
            appliedRowFilters={appliedRowFilters}
            handleSort={handleDynamicSort}
            resetDynamicSort={resetDynamicSort}
            isInMore
          />
        </MenuNested>
      ) : null}
    </MenuNested>
  );
}

CertsControlsMore.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string),
  setIsMoreOpen: PropTypes.func.isRequired,
  // Sort
  handleDynamicSort: PropTypes.func.isRequired,
  resetDynamicSort: PropTypes.func.isRequired,
  // Group By
  groupByOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  // Filter
  showFilterBar: PropTypes.bool.isRequired,
  setShowFilterBar: PropTypes.func.isRequired,
  // Rows
  rowFilters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  appliedRowFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  setAppliedRowFilters: PropTypes.func.isRequired,
  // Columns
  colFilters: COL_FILTERS_MODEL.isRequired,
  setColFilters: PropTypes.func.isRequired,
  appliedColFilters: APPLIED_COL_FILTERS_MODEL,
  setAppliedColFilters: PropTypes.func.isRequired,
  setIsDraggingDndItem: PropTypes.func.isRequired,
};

CertsControlsMore.defaultProps = { options: [], appliedColFilters: null };

export default CertsControlsMore;
